import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { lowStockAlertsAPI } from '@/services/api';
import api from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  AlertTriangle, Search, RefreshCw, Package, 
  Save, Settings, Bell, BellOff, Check, X
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const LowStockSettings = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [settings, setSettings] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [filterHasSetting, setFilterHasSetting] = useState('all');
  
  // Bulk edit state
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAlertQty, setBulkAlertQty] = useState(10);
  const [bulkEnabled, setBulkEnabled] = useState(true);
  const [savingBulk, setSavingBulk] = useState(false);
  
  // Single edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [editAlertQty, setEditAlertQty] = useState(0);
  const [editEnabled, setEditEnabled] = useState(true);
  const [savingEdit, setSavingEdit] = useState(false);

  const storeId = user?.entity_id;

  const fetchSettings = useCallback(async () => {
    if (!storeId) return;
    
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (selectedCategory && selectedCategory !== 'all') params.category_id = selectedCategory;
      if (selectedBrand && selectedBrand !== 'all') params.brand_id = selectedBrand;
      if (filterHasSetting && filterHasSetting !== 'all') {
        params.has_setting = filterHasSetting === 'custom';
      }
      params.limit = 200;
      
      const response = await lowStockAlertsAPI.getStoreSettings(storeId, params);
      setSettings(response.data.items || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({ title: 'Error', description: 'Failed to fetch low stock settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [storeId, search, selectedCategory, selectedBrand, filterHasSetting, toast]);

  const fetchFilters = useCallback(async () => {
    try {
      const [catRes, brandRes] = await Promise.all([
        api.get('/categories?limit=500'),
        api.get('/brands?limit=500')
      ]);
      setCategories(catRes.data || []);
      setBrands(brandRes.data || []);
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  }, []);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSettings();
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCategory('all');
    setSelectedBrand('all');
    setFilterHasSetting('all');
    setSelectedProducts([]);
  };

  const toggleSelectProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedProducts.length === settings.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(settings.map(s => s.product_id));
    }
  };

  const openEditDialog = (product) => {
    setEditProduct(product);
    setEditAlertQty(product.alert_qty);
    setEditEnabled(product.is_enabled);
    setEditDialogOpen(true);
  };

  const handleSaveSingle = async () => {
    if (!editProduct) return;
    
    setSavingEdit(true);
    try {
      await lowStockAlertsAPI.updateStoreSetting(storeId, editProduct.product_id, {
        alert_qty: editAlertQty,
        is_enabled: editEnabled
      });
      
      toast({ title: 'Success', description: `Alert threshold updated for ${editProduct.product_name}` });
      setEditDialogOpen(false);
      fetchSettings();
    } catch (error) {
      console.error('Error saving setting:', error);
      toast({ title: 'Error', description: 'Failed to save setting', variant: 'destructive' });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleResetToDefault = async (product) => {
    if (!product.has_custom_setting) return;
    
    try {
      await lowStockAlertsAPI.deleteStoreSetting(storeId, product.product_id);
      toast({ title: 'Success', description: `Reset to default threshold for ${product.product_name}` });
      fetchSettings();
    } catch (error) {
      console.error('Error resetting setting:', error);
      toast({ title: 'Error', description: 'Failed to reset setting', variant: 'destructive' });
    }
  };

  const handleBulkSave = async () => {
    if (selectedProducts.length === 0) return;
    
    setSavingBulk(true);
    try {
      await lowStockAlertsAPI.bulkUpdateStoreSettings(storeId, {
        product_ids: selectedProducts,
        alert_qty: bulkAlertQty,
        is_enabled: bulkEnabled
      });
      
      toast({ 
        title: 'Success', 
        description: `Updated alert threshold for ${selectedProducts.length} products` 
      });
      setBulkDialogOpen(false);
      setSelectedProducts([]);
      fetchSettings();
    } catch (error) {
      console.error('Error bulk saving:', error);
      toast({ title: 'Error', description: 'Failed to save settings', variant: 'destructive' });
    } finally {
      setSavingBulk(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="low-stock-settings-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-6 h-6 text-amber-600" />
            Low Stock Alert Settings
          </h1>
          <p className="text-gray-500 mt-1">Configure product-wise low stock alert thresholds for your store</p>
        </div>
        <div className="flex gap-2">
          {selectedProducts.length > 0 && (
            <Button 
              onClick={() => setBulkDialogOpen(true)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="bulk-edit-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Bulk Edit ({selectedProducts.length})
            </Button>
          )}
          <Button onClick={fetchSettings} variant="outline" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Products</p>
                <p className="text-xl font-bold text-gray-900">{summary.total_products || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Settings className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Custom Settings</p>
                <p className="text-xl font-bold text-gray-900">{summary.products_with_custom_settings || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Low Stock Items</p>
                <p className="text-xl font-bold text-amber-600">{summary.low_stock_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Check className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Selected</p>
                <p className="text-xl font-bold text-gray-900">{selectedProducts.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-gray-600 text-sm mb-1 block">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Product name or SKU..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            <div className="w-[180px]">
              <Label className="text-gray-600 text-sm mb-1 block">Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[180px]">
              <Label className="text-gray-600 text-sm mb-1 block">Brand</Label>
              <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                <SelectTrigger data-testid="brand-filter">
                  <SelectValue placeholder="All Brands" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Brands</SelectItem>
                  {brands.map(brand => (
                    <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[180px]">
              <Label className="text-gray-600 text-sm mb-1 block">Settings Filter</Label>
              <Select value={filterHasSetting} onValueChange={setFilterHasSetting}>
                <SelectTrigger data-testid="settings-filter">
                  <SelectValue placeholder="All Products" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Products</SelectItem>
                  <SelectItem value="custom">With Custom Settings</SelectItem>
                  <SelectItem value="default">Using Default</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button type="submit" data-testid="search-btn">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            
            <Button type="button" variant="outline" onClick={clearFilters}>
              Clear
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Settings Table */}
      <Card>
        <CardHeader className="border-b bg-gray-50/50 py-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-600" />
            Product Alert Thresholds
          </CardTitle>
          <CardDescription>
            Set custom low stock alert quantities for each product. Products without custom settings use the default threshold.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[50px]">
                    <Checkbox 
                      checked={selectedProducts.length === settings.length && settings.length > 0}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Current Stock</TableHead>
                  <TableHead className="text-right">Alert Threshold</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Enabled</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      Loading settings...
                    </TableCell>
                  </TableRow>
                ) : settings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      No products found
                    </TableCell>
                  </TableRow>
                ) : (
                  settings.map((item) => (
                    <TableRow 
                      key={item.product_id} 
                      className={item.is_low_stock ? 'bg-amber-50' : ''}
                      data-testid={`product-row-${item.product_id}`}
                    >
                      <TableCell>
                        <Checkbox 
                          checked={selectedProducts.includes(item.product_id)}
                          onCheckedChange={() => toggleSelectProduct(item.product_id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-gray-900">{item.product_name}</p>
                          <p className="text-sm text-gray-500">{item.sku}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600">{item.category_name || '-'}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={item.current_stock <= 0 ? 'text-red-600 font-semibold' : 'text-gray-900'}>
                          {item.current_stock}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className="font-medium">{item.alert_qty}</span>
                          {item.has_custom_setting ? (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                              Custom
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs bg-gray-50 text-gray-500">
                              Default
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {item.is_low_stock ? (
                          <Badge className="bg-amber-100 text-amber-700 gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Low Stock
                          </Badge>
                        ) : item.current_stock <= 0 ? (
                          <Badge variant="destructive" className="gap-1">
                            <X className="w-3 h-3" />
                            Out
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            OK
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {item.is_enabled ? (
                          <Bell className="w-4 h-4 text-emerald-600 mx-auto" />
                        ) : (
                          <BellOff className="w-4 h-4 text-gray-400 mx-auto" />
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => openEditDialog(item)}
                            data-testid={`edit-btn-${item.product_id}`}
                          >
                            <Settings className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          {item.has_custom_setting && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleResetToDefault(item)}
                              className="text-gray-500 hover:text-gray-700"
                              title="Reset to default"
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Single Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-600" />
              Edit Alert Threshold
            </DialogTitle>
            <DialogDescription>
              Set custom low stock alert threshold for this product
            </DialogDescription>
          </DialogHeader>
          
          {editProduct && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900">{editProduct.product_name}</p>
                <p className="text-sm text-gray-500">SKU: {editProduct.sku}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Current Stock: <span className="font-medium">{editProduct.current_stock}</span>
                </p>
                <p className="text-sm text-gray-500">
                  Default Threshold: <span className="font-medium">{editProduct.default_alert_qty}</span>
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Alert Threshold Quantity</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={editAlertQty}
                  onChange={(e) => setEditAlertQty(parseFloat(e.target.value) || 0)}
                  placeholder="Enter threshold quantity"
                  data-testid="edit-alert-qty-input"
                />
                <p className="text-xs text-gray-500">
                  Alert will trigger when stock falls to or below this quantity
                </p>
              </div>
              
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">Enable Alert</p>
                  <p className="text-sm text-gray-500">Turn off to disable alerts for this product</p>
                </div>
                <Switch 
                  checked={editEnabled} 
                  onCheckedChange={setEditEnabled}
                  data-testid="edit-enabled-switch"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSingle} 
              disabled={savingEdit}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="save-single-btn"
            >
              {savingEdit ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-600" />
              Bulk Edit Alert Thresholds
            </DialogTitle>
            <DialogDescription>
              Apply the same alert threshold to {selectedProducts.length} selected products
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>{selectedProducts.length}</strong> products selected for bulk update
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Alert Threshold Quantity</Label>
              <Input
                type="number"
                min="0"
                step="1"
                value={bulkAlertQty}
                onChange={(e) => setBulkAlertQty(parseFloat(e.target.value) || 0)}
                placeholder="Enter threshold quantity"
                data-testid="bulk-alert-qty-input"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Enable Alerts</p>
                <p className="text-sm text-gray-500">Enable alerts for all selected products</p>
              </div>
              <Switch 
                checked={bulkEnabled} 
                onCheckedChange={setBulkEnabled}
                data-testid="bulk-enabled-switch"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkSave} 
              disabled={savingBulk}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="save-bulk-btn"
            >
              {savingBulk ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Apply to All
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LowStockSettings;
